/// <amd-module name="Search/Wiki/Components/ZeroData" />

import * as React from "react";
import { format } from "VSS/Core/Util/String";
import * as Container from "Search/Shared/Base/Container";
import * as ZeroData from "Search/Shared/Components/ZeroData";
import * as Constants from "Search/Wiki/Constants";
import * as Resources from "Search/Wiki/Resources";
import * as SharedResources from "Search/Shared/Resources";
import * as Utils_Accessibility from 'VSS/Core/Util/Accessibility';
import { FormatComponent } from "VSS/Platform/Components/Format";
import { ScenarioType } from "Search/Shared/Components/ZeroData/ZeroData.Props";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { ActionCreator } from "Search/Wiki/Flux/ActionCreator";
import { StoreName, StoresHub, IAggregatedState } from "Search/Wiki/Flux/StoresHub";

export const ZeroDataContainer = Container.create<StoreName, ActionCreator, IAggregatedState, StoresHub>(
    [
        "zeroDataStore"
    ],
    ({
        searchStoreState,
        zeroDataState
    }, props) => {
        if (zeroDataState.scenario && (zeroDataState.scenario !== ZeroData.ScenarioType.None)) {
            const { query } = searchStoreState;
            const { notifyResultsRendered, publishZeroData, notifyFeedbackMailLinkClicked } = props.actionCreator
            let zeroDataProps = {} as ZeroData.IZeroDataProps;

            if (zeroDataState.scenario === ZeroData.ScenarioType.LandingPage) {
                zeroDataProps.message = Resources.WikiWelcomeText;
                zeroDataProps.help = "";
                zeroDataProps.announceMessage = Resources.WikiWelcomeText;
            } else {
                zeroDataProps = getZeroDataProps(
                    props.pageContext,
                    query.searchText,
                    zeroDataState.scenario,
                    notifyFeedbackMailLinkClicked);
            }

            return <ZeroData.ZeroData
                {...zeroDataProps}
                onDidMount={() => {
                    Utils_Accessibility.announce(zeroDataProps.announceMessage, true);
                    notifyResultsRendered();
                    publishZeroData(ZeroData.ScenarioType[zeroDataProps.scenario]);
                }} />;
        }

        return null;
    });

function getZeroDataProps(
    pageContext: IVssPageContext,
    queryText: string,
    scenario: ScenarioType,
    _notifyFeedbackMailLinkClicked: () => void): ZeroData.IZeroDataProps {
    let zeroDataProps = {} as ZeroData.IZeroDataProps;
    const pageService = pageContext.getService<IVssPageService>("IVssPageService");
    const pageData = pageService.getData();
    const isHosted: boolean = pageData!.isHosted;
    zeroDataProps.pageContext = pageContext;

    if (scenario === ZeroData.ScenarioType.ServiceError) {
        const messageText: string = SharedResources.ServiceErrorMessage;
        const feedbackLink = isHosted
            ? Constants.CodexFeedbackMailToLinkFormat
            : Constants.FeedbackMailToLinkFormat;
        zeroDataProps.scenario = ZeroData.ScenarioType.ServiceError;
        zeroDataProps.message = messageText;
        zeroDataProps.help = ZeroData.getServiceErrorHelp(isHosted, feedbackLink);
        zeroDataProps.announceMessage = SharedResources.ServiceErrorMessage;
    } else {
        zeroDataProps.scenario = scenario;

        if (zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResults) {
            const feedbackLink = isHosted
                ? Constants.CodexFeedbackMailToLinkFormat
                : Constants.FeedbackMailToLinkFormat;
            const learnMoreLinkAriaLabel = format(SharedResources.LearnMoreAriaLabel, Resources.WikiSearchAriaLabel);
            const message: JSX.Element =
                <FormatComponent format={Resources.ZeroWikiResultsMessageFormat}>
                    {
                        <span className="searchText">
                            {queryText}
                        </span>
                    }
                </FormatComponent>;

            const help: JSX.Element = ZeroData.getZeroResultsHelpComponent(
                isHosted,
                SharedResources.ZeroResultsHelpText,
                Resources.ZeroWikiResultsLearnMoreFormat,
                Constants.LearnMoreLink,
                learnMoreLinkAriaLabel,
                feedbackLink,
                _notifyFeedbackMailLinkClicked);

            zeroDataProps.message = message;
            zeroDataProps.help = help;
            zeroDataProps.announceMessage = format(Resources.ZeroWikiResultsMessageFormat, queryText);
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.PrefixWildCardNotSupported) {
            zeroDataProps.message = ZeroData.getQueryNotSupportedHelp(queryText, SharedResources.WildCardNotSupportedHelpFormat);
            zeroDataProps.help = SharedResources.WildCardNotSupportedMessage;
            zeroDataProps.announceMessage = format(SharedResources.WildCardNotSupportedHelpFormat, queryText);
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.NoPermission) {
            zeroDataProps.message = Resources.ZeroWikiResultsNoPermissionMessage;
            zeroDataProps.help = ZeroData.getAccessPermissionHelp();
            zeroDataProps.announceMessage = Resources.ZeroWikiResultsNoPermissionMessage;;
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.IndexingScenario) {
            const feedbackLink = isHosted
                ? Constants.CodexFeedbackMailToLinkFormat
                : Constants.FeedbackMailToLinkFormat;
            zeroDataProps.message = SharedResources.AccountIndexingMessage;
            zeroDataProps.help = ZeroData.getIndexingHelp(isHosted, feedbackLink);
            zeroDataProps.announceMessage = SharedResources.AccountIndexingMessage;
        }
    }

    return zeroDataProps;
}
